import React from "react";
import { Card, Alert } from "react-bootstrap";

function AssigningRunbooksToGroups() {
  return (
    <div>
      <h2 className="mb-4">
        Assigning Runbooks to Groups
      </h2>

      <Alert variant="info">
        Learn how to control which runbooks members of a permission group can access.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Why Assign Runbooks to Groups?</h4>
          <p>
            Assigning runbooks to groups is how you control what workflows different teams can see and use.
            Even if a user has permission to execute runbooks, they won't see any runbooks in the portal
            unless those runbooks are assigned to one of their groups.
          </p>
          <p className="mt-3">
            <strong>Key Concept:</strong> Runbook access is <em>additive</em>. If a user is in multiple groups,
            they see all runbooks from all their groups combined.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Step-by-Step: Assigning Runbooks</h4>
          <ol>
            <li>Go to <strong>Permissions</strong> in the Settings section</li>
            <li>Click on a permission group in the table to select it</li>
            <li>Click the <strong>Manage Runbooks</strong> button</li>
            <li>A modal will open showing two columns:
              <ul className="mt-2">
                <li><strong>Available Runbooks</strong> (left) - All runbooks not yet assigned to this group</li>
                <li><strong>Assigned Runbooks</strong> (right) - Runbooks currently assigned to this group</li>
              </ul>
            </li>
            <li>To add a runbook, click the <strong>+</strong> button next to it in the Available Runbooks column</li>
            <li>To remove a runbook, click the <strong>trash icon</strong> next to it in the Assigned Runbooks column</li>
            <li>Changes take effect immediately - no save button required</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Searching for Runbooks</h4>
          <p>
            The Available Runbooks list includes a search box at the top. You can search by:
          </p>
          <ul>
            <li><strong>Runbook Name</strong></li>
            <li><strong>Description</strong></li>
            <li><strong>Runbook ID</strong></li>
          </ul>
          <p className="mt-3">
            <strong>Tip:</strong> Use the search to quickly find specific runbooks when you have many workflows in your organization.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding Runbook Visibility</h4>
          <p>
            When a user logs in, the system determines which runbooks they can see by:
          </p>
          <ol>
            <li>Finding all permission groups the user belongs to</li>
            <li>Collecting all runbooks assigned to any of those groups</li>
            <li>Removing duplicates (if multiple groups have the same runbook)</li>
            <li>Displaying the combined list</li>
          </ol>
          <Alert variant="warning" className="mt-3">
            <strong>Important:</strong> A user must be in at least one group that has runbooks assigned. Otherwise,
            they'll see an empty portal with no workflows available.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Runbook Assignment Patterns</h4>

          <h6 className="mt-3">By Department</h6>
          <p><strong>Each department sees only their runbooks:</strong></p>
          <ul>
            <li>Finance Team → Finance-related runbooks (payroll, reporting, invoicing)</li>
            <li>HR Department → HR runbooks (onboarding, offboarding, benefits)</li>
            <li>IT Operations → IT runbooks (provisioning, monitoring, backups)</li>
          </ul>

          <h6 className="mt-3">By Access Level</h6>
          <p><strong>Give broader or narrower access based on role:</strong></p>
          <ul>
            <li>Executives → High-level dashboard and reporting runbooks only</li>
            <li>Operators → All production runbooks they need to execute</li>
            <li>Developers → All runbooks (for testing and development)</li>
          </ul>

          <h6 className="mt-3">By Project or Function</h6>
          <p><strong>Create groups around specific business processes:</strong></p>
          <ul>
            <li>"Month-End Close Team" → Only month-end financial close runbooks</li>
            <li>"Customer Onboarding" → Runbooks for provisioning new customers</li>
            <li>"Reporting Users" → Only reporting and dashboard generation workflows</li>
          </ul>

          <h6 className="mt-3">Shared Access</h6>
          <p><strong>Some runbooks assigned to multiple groups:</strong></p>
          <ul>
            <li>Common utilities (data exports, basic reports) → Assigned to many groups</li>
            <li>Department-specific workflows → Assigned to one group only</li>
            <li>This gives flexibility without duplication</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Example Scenario</h4>
          <p><strong>Finance Team with 50 total runbooks in your organization:</strong></p>
          <ol>
            <li>You have a "Finance Team" permission group</li>
            <li>You assign only the 8 finance-related runbooks to this group</li>
            <li>Finance team members log in and see only those 8 runbooks</li>
            <li>They cannot see the other 42 runbooks (HR, IT, Sales, etc.)</li>
          </ol>
          <p className="mt-3">
            <strong>Multi-Group Example:</strong> Sarah is in both "Finance Team" and "Developers" groups.
          </p>
          <ul>
            <li>Finance Team has 8 runbooks assigned</li>
            <li>Developers has 30 runbooks assigned (including 2 that overlap with Finance)</li>
            <li>Sarah sees 36 unique runbooks (8 + 30 - 2 duplicates)</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Best Practices</h4>
          <ul>
            <li><strong>Start Narrow</strong> - Assign only the runbooks users definitely need, then add more as requested</li>
            <li><strong>Use Descriptive Runbook Names</strong> - Makes it easier to identify what to assign</li>
            <li><strong>Group by Purpose</strong> - Keep related runbooks together in the same groups</li>
            <li><strong>Regular Reviews</strong> - Quarterly, review which runbooks are assigned to which groups</li>
            <li><strong>Document Intent</strong> - Use group descriptions to note what types of runbooks should be assigned</li>
            <li><strong>Test Access</strong> - After assignment, have a group member verify they can see the runbooks</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Managing Runbook Assignments</h4>

          <h6 className="mt-3">Adding Multiple Runbooks</h6>
          <p>To assign many runbooks at once:</p>
          <ol>
            <li>Keep the Manage Runbooks modal open</li>
            <li>Click the + button for each runbook you want to add</li>
            <li>Each click assigns immediately - no batch operation needed</li>
          </ol>

          <h6 className="mt-3">Removing Runbooks</h6>
          <p>To remove runbook access:</p>
          <ol>
            <li>Find the runbook in the Assigned Runbooks column (right side)</li>
            <li>Click the trash icon</li>
            <li>The runbook is immediately removed from the group</li>
            <li>Users in that group can no longer see it (unless it's assigned via another group)</li>
          </ol>

          <h6 className="mt-3">Checking Assignments</h6>
          <p>To see which groups have access to a specific runbook:</p>
          <ul>
            <li>Go to the <strong>Runbook Access Lookup</strong> tab in Permissions</li>
            <li>Select the runbook from the dropdown</li>
            <li>View which groups have access and which users can see it</li>
            <li>See detailed information about all users with access through their group memberships</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>When to Create New Groups vs Assign More Runbooks</h4>

          <h6 className="mt-3">Add Runbooks to Existing Group When:</h6>
          <ul>
            <li>The new runbook fits the group's purpose</li>
            <li>All group members should see the new runbook</li>
            <li>It's similar to other runbooks already assigned</li>
          </ul>

          <h6 className="mt-3">Create a New Group When:</h6>
          <ul>
            <li>Only a subset of users need access</li>
            <li>The runbook is for a different department or function</li>
            <li>Different permission levels are needed</li>
            <li>You want to restrict access more granularly</li>
          </ul>

          <p className="mt-3">
            <strong>Example:</strong> Your "Finance Team" group has 10 runbooks. You create a new payroll automation
            runbook. If all finance team members should see it, add it to the existing group. If only 3 payroll
            specialists should see it, create a "Payroll Specialists" group and assign it there.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Troubleshooting</h4>

          <h6 className="mt-3">User Can't See a Runbook</h6>
          <ul>
            <li>Check if the user is in at least one active group</li>
            <li>Verify the runbook is assigned to one of their groups</li>
            <li>Confirm the group is marked as Active</li>
            <li>Have the user refresh their browser</li>
          </ul>

          <h6 className="mt-3">User Sees Too Many Runbooks</h6>
          <ul>
            <li>Check all groups the user belongs to</li>
            <li>They see the combined runbooks from all groups</li>
            <li>Remove them from unnecessary groups or remove runbooks from those groups</li>
          </ul>

          <h6 className="mt-3">Can't Find a Runbook to Assign</h6>
          <ul>
            <li>Use the search box in the Available Runbooks column</li>
            <li>Check if it's already assigned (look in Assigned Runbooks column)</li>
            <li>Verify the runbook exists in your organization</li>
          </ul>

          <h6 className="mt-3">Changes Not Taking Effect</h6>
          <ul>
            <li>Changes are immediate - no save button needed</li>
            <li>Have the user refresh their browser (Ctrl+R or Cmd+R)</li>
            <li>Verify the group is Active</li>
            <li>Check if the user is actually in the group</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Security Considerations</h4>
          <p>
            Runbook assignments are a critical security boundary. Consider these points:
          </p>
          <ul>
            <li><strong>Sensitive Workflows</strong> - Assign sensitive runbooks (like production deployments) to restricted groups</li>
            <li><strong>Least Privilege</strong> - Only assign runbooks that users need for their job function</li>
            <li><strong>Regular Audits</strong> - Review assignments quarterly to remove unnecessary access</li>
            <li><strong>Separation of Duties</strong> - Consider separating high-risk workflows into different groups</li>
            <li><strong>Document Decisions</strong> - Keep notes on why specific runbooks were assigned to specific groups</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Working with New Runbooks</h4>
          <p>
            When you or your team creates a new runbook:
          </p>
          <ol>
            <li>The runbook is initially not assigned to any groups</li>
            <li>No users can see it (except Admins who see all runbooks)</li>
            <li>Assign it to appropriate groups to make it visible</li>
            <li>Test by having a group member verify they can see and execute it</li>
          </ol>
          <p className="mt-3">
            <strong>Best Practice:</strong> Create a "Testing" or "Developers" group that gets assigned all new runbooks
            by default for testing before assigning to production user groups.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AssigningRunbooksToGroups;
